import {
  clampQcLaborExpectedPageFromPathname,
  clampQcLaborPageKeyForJobSlug,
  formatLaborLocationLabel,
  laborPageKeysEquivalent,
} from "@/pages/clampqc/clampQcLaborClockState";
import {
  configurationLaborExpectedPageFromPathname,
  configurationLaborPageKeyForAlternate,
  configurationLaborPageKeyForBatchItem,
  CONFIGURATION_LIST_LABOR_PAGE,
} from "@/pages/manufacturing/configurationLaborPage";
import {
  assemblyLaborExpectedPageFromPathname,
  ASSEMBLY_LIST_LABOR_PAGE,
  assemblyLaborPageKeyForWorkOrder,
} from "@/pages/manufacturing/assemblyLaborPage";
import { prePackLaborExpectedPageFromPathname, prePackLaborPageKeyForCarton } from "@/pages/prepack/prepackLaborPage";
import {
  transferPackLaborExpectedPageFromPathname,
  transferPackLaborPageKeyForOrder,
} from "@/pages/prepack/prepackTransferLaborPage";
import {
  kittingPickLaborExpectedPageFromPathname,
  KITTING_LIST_LABOR_PAGE,
} from "@/pages/manufacturing/kittingLaborPage";
import {
  ENCAPSULATION_LABOR_PAGE,
  encapsulationLaborPageKey,
  isEncapsulationActivity,
} from "@/pages/manufacturing/encapsulationLaborPage";
import { inboundLaborExpectedPageFromPathname } from "@/pages/shipping/inboundLaborPage";

export const HUB_MENU_SHELL_LABOR_PAGE = "hub-overview";
export const SHIPPING_LABOR_PAGE = "shipping-team";
export const FINAL_QC_LABOR_PAGE = "final-qc";
export const PRE_ENCAPSULATION_LABOR_PAGE = "pre-encapsulation";
export { ENCAPSULATION_LABOR_PAGE };
export { KITTING_LIST_LABOR_PAGE };
/** Canonical Config station name in Postgres (`labor.device_session.page`). */
export const CONFIGURATION_LABOR_PAGE = "config";
/** Canonical Cal+Test production station (`labor.device_session.page`). */
export const PRODUCTION_LABOR_PAGE = "cal+test";
/** Wireless portal check station between Configuration and Cal+Test. */
export const WIRELESS_CHECK_LABOR_PAGE = "wireless-check";

export {
  clampQcLaborExpectedPageFromPathname,
  clampQcLaborPageKeyForJobSlug,
  formatLaborLocationLabel,
  laborPageKeysEquivalent,
  configurationLaborExpectedPageFromPathname,
  configurationLaborPageKeyForAlternate,
  configurationLaborPageKeyForBatchItem,
  CONFIGURATION_LIST_LABOR_PAGE,
  assemblyLaborExpectedPageFromPathname,
  ASSEMBLY_LIST_LABOR_PAGE,
  assemblyLaborPageKeyForWorkOrder,
  prePackLaborExpectedPageFromPathname,
  prePackLaborPageKeyForCarton,
  transferPackLaborExpectedPageFromPathname,
  transferPackLaborPageKeyForOrder,
  kittingPickLaborExpectedPageFromPathname,
};

export function hubLaborExpectedPageFromPathname(pathname: string, search = ""): string {
  if (pathname.startsWith("/clamp-qc")) {
    return clampQcLaborExpectedPageFromPathname(pathname);
  }
  if (pathname.startsWith("/shipping/pre-pack/transfers/")) {
    return transferPackLaborExpectedPageFromPathname(pathname);
  }
  if (pathname.startsWith("/shipping/pre-pack")) {
    return prePackLaborExpectedPageFromPathname(pathname);
  }
  if (pathname.startsWith("/manufacturing/final-qc")) {
    return FINAL_QC_LABOR_PAGE;
  }
  if (pathname.startsWith("/manufacturing/configuration")) {
    return configurationLaborExpectedPageFromPathname(pathname, search);
  }
  if (pathname.startsWith("/manufacturing/assembly")) {
    return assemblyLaborExpectedPageFromPathname(pathname);
  }
  if (pathname.startsWith("/manufacturing/wireless-check")) {
    return WIRELESS_CHECK_LABOR_PAGE;
  }
  if (pathname.startsWith("/manufacturing/cal-test") || pathname.startsWith("/manufacturing/production")) {
    return PRODUCTION_LABOR_PAGE;
  }
  if (pathname.startsWith("/manufacturing/pre-encapsulation")) {
    return PRE_ENCAPSULATION_LABOR_PAGE;
  }
  if (pathname.startsWith("/manufacturing/encapsulation")) {
    const activity = new URLSearchParams(search).get("activity") ?? "";
    if (isEncapsulationActivity(activity)) {
      return encapsulationLaborPageKey(activity);
    }
    return ENCAPSULATION_LABOR_PAGE;
  }
  if (pathname.startsWith("/manufacturing/kitting")) {
    return kittingPickLaborExpectedPageFromPathname(pathname, search);
  }
  const inboundLaborPage = inboundLaborExpectedPageFromPathname(pathname);
  if (inboundLaborPage != null) {
    return inboundLaborPage;
  }
  if (pathname.startsWith("/shipping")) {
    return SHIPPING_LABOR_PAGE;
  }
  return HUB_MENU_SHELL_LABOR_PAGE;
}

/** True when this `labor.device_session.page` key is a clocked-in working surface (not list/overview). */
export function isWorkingSubpageKey(pageKey: string): boolean {
  const key = pageKey.trim();
  if (key.startsWith("clamp-qc-job:")) return true;
  if (key.startsWith("pre-pack-job:")) return true;
  if (key.startsWith("transfer-pack-job:")) return true;
  if (key === FINAL_QC_LABOR_PAGE) return true;
  if (key.startsWith("config-job:")) return true;
  if (key.startsWith("assembly-job:")) return true;
  if (key === PRODUCTION_LABOR_PAGE) return true;
  if (key === WIRELESS_CHECK_LABOR_PAGE) return true;
  if (key === PRE_ENCAPSULATION_LABOR_PAGE) return true;
  if (key.startsWith("encapsulation-job:")) return true;
  if (key.startsWith("kitting-pick-run:")) return true;
  if (key.startsWith("kitting-pick:")) return true;
  if (key === SHIPPING_LABOR_PAGE) return true;
  if (key.startsWith("receiving-job:")) {
    // Browse-only receiving surface: receive queue overview (not Arrivals modal work).
    if (key === "receiving-job:receive") {
      return false;
    }
    return true;
  }
  return false;
}

/**
 * Inverse of `hubLaborExpectedPageFromPathname` for known working keys only (used by lockdown interstitial).
 */
export function workingUrlForSessionPage(pageKey: string): string | null {
  const key = pageKey.trim();
  if (key.startsWith("clamp-qc-job:")) {
    const slug = key.slice("clamp-qc-job:".length).trim();
    if (slug === "") return null;
    return `/clamp-qc/jobs/${encodeURIComponent(slug)}`;
  }
  const prePackMatch = /^pre-pack-job:(\d+):(\d+)$/.exec(key);
  if (prePackMatch) {
    const shipmentId = prePackMatch[1];
    const cartonNumber = prePackMatch[2];
    return `/shipping/pre-pack/shipments/${shipmentId}/cartons/${cartonNumber}`;
  }
  const transferPackMatch = /^transfer-pack-job:(\d+)$/.exec(key);
  if (transferPackMatch) {
    return `/shipping/pre-pack/transfers/${transferPackMatch[1]}`;
  }
  const configBuildMatch = /^config-job:(\d+)$/.exec(key);
  if (configBuildMatch) {
    return `/manufacturing/configuration/build/${configBuildMatch[1]}`;
  }
  if (key === "config-job:alternate") {
    return "/manufacturing/configuration/alternate";
  }
  const configAlternateMatch = /^config-job:alternate:(.+)$/.exec(key);
  if (configAlternateMatch) {
    const sku = configAlternateMatch[1] ?? "";
    if (sku !== "") {
      return `/manufacturing/configuration/alternate?sku=${encodeURIComponent(sku)}`;
    }
    return "/manufacturing/configuration/alternate";
  }
  if (key === FINAL_QC_LABOR_PAGE) return "/manufacturing/final-qc";
  if (key === PRODUCTION_LABOR_PAGE) return "/manufacturing/cal-test";
  if (key === PRE_ENCAPSULATION_LABOR_PAGE) return "/manufacturing/pre-encapsulation";
  if (key.startsWith("encapsulation-job:")) {
    const activity = key.slice("encapsulation-job:".length).trim();
    if (isEncapsulationActivity(activity)) {
      return `/manufacturing/encapsulation?activity=${activity}`;
    }
    return "/manufacturing/encapsulation";
  }
  if (key === ENCAPSULATION_LABOR_PAGE) return "/manufacturing/encapsulation";
  const assemblyMatch = /^assembly-job:(\d+)$/.exec(key);
  if (assemblyMatch) {
    return `/manufacturing/assembly/work-orders/${assemblyMatch[1]}`;
  }
  if (key === SHIPPING_LABOR_PAGE) return "/shipping/outbound";
  if (key === "receiving-job:arrivals") return "/shipping/arrivals";
  if (key === "receiving-job:receive") return "/shipping/receive";
  if (key === "receiving-job:holding") return "/shipping/holding";
  if (key === "receiving-job:to-stock") return "/shipping/to-stock";
  const receiveOrderMatch = /^receiving-job:receive:(purchase_order|transfer_order):(\d+)$/.exec(key);
  if (receiveOrderMatch) {
    return `/shipping/receive/${receiveOrderMatch[1]}/${receiveOrderMatch[2]}`;
  }
  if (key.startsWith("kitting-pick-run:")) {
    const runId = key.slice("kitting-pick-run:".length).trim();
    if (runId === "") return "/manufacturing/kitting";
    return `/manufacturing/kitting/pick?run=${encodeURIComponent(runId)}`;
  }
  if (key.startsWith("kitting-pick:")) {
    const slotList = key.slice("kitting-pick:".length).trim();
    if (slotList === "") return "/manufacturing/kitting";
    return `/manufacturing/kitting/pick?slots=${encodeURIComponent(slotList)}`;
  }
  return null;
}

